/* KOMTO redesign (BAN-179): behaviours of the page sections (komto-page-*, About, FAQ, Contact,
 * Assembly videos, All categories, Reviews). Loaded by the sections that need it, so it guards
 * against running twice.
 * - Background video (About A-02): plays muted on desktop only, never with reduced motion; a visible
 *   pause / play button; on phones the poster only (no video download) unless the section says so.
 * - "Chat with us" buttons ([data-komto-chat]) open Shopify Inbox; with the chat off they stay links.
 * - FAQ (F-01…F-03): search filters the questions, the side nav marks the group in view.
 * - Assembly videos (V-01, V-03…V-06): search, category tabs, and the video player (S-08).
 * - Reviews page (R-01): "Write a review" opens the Judge.me form.
 */
(() => {
  if (window.komtoPagesLoaded) return;
  window.komtoPagesLoaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 769px)');

  /* Background video -------------------------------------------------------- */
  const initVideo = (root) => {
    const video = root.querySelector('video[data-src]');
    const toggle = root.querySelector('[data-kp-video-toggle]');
    if (!video || !toggle) return;
    const mobileVideo = root.dataset.mobile === 'video';

    const setState = (playing) => {
      root.classList.toggle('is-playing', playing);
      root.classList.toggle('is-paused', !playing);
      toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
    };

    const load = () => {
      if (video.getAttribute('src')) return;
      video.src = video.dataset.src;
      video.addEventListener('loadeddata', () => root.classList.add('is-loaded'), { once: true });
    };

    const play = () => {
      load();
      const attempt = video.play();
      if (attempt && typeof attempt.then === 'function') {
        attempt.then(() => setState(true)).catch(() => setState(false));
      } else {
        setState(true);
      }
    };

    const start = () => {
      if (!desktop.matches && !mobileVideo) {
        // Phone: the poster only, no video file and no button.
        if (!video.paused) video.pause();
        toggle.hidden = true;
        root.classList.remove('is-playing', 'is-paused');
        return;
      }
      toggle.hidden = false;
      if (reducedMotion.matches || root.dataset.userPaused === 'true') {
        setState(false);
        return;
      }
      play();
    };

    toggle.addEventListener('click', () => {
      if (video.paused) {
        root.dataset.userPaused = 'false';
        play();
      } else {
        root.dataset.userPaused = 'true';
        video.pause();
        setState(false);
      }
    });

    // Don't play while the band is off screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!video.getAttribute('src')) return;
          if (!entry.isIntersecting && !video.paused) video.pause();
          else if (entry.isIntersecting && video.paused && root.classList.contains('is-playing')) video.play().catch(() => {});
        });
      }).observe(root);
    }

    desktop.addEventListener('change', start);
    start();
  };

  /* Chat -------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-komto-chat]');
    if (!button || event.defaultPrevented) return;
    const chat = document.querySelector('shopify-chat');
    if (chat && typeof chat.show === 'function') {
      event.preventDefault();
      chat.show();
    }
  });

  /* Search helpers ------------------------------------------------------------ */
  const normalize = (text) =>
    (text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  const matches = (haystack, query) => query.split(' ').every((word) => haystack.includes(word));

  /* Marks the nav link of the group in view (FAQ topics): the last group whose top has passed
     a line at 30 % of the viewport, or the first one near the top of the page. */
  const spy = (links, targets, onChange) => {
    if (!links.length || !targets.length) return;
    let current = null;
    const update = () => {
      const line = window.innerHeight * 0.3;
      const visible = targets.filter((target) => !target.hidden);
      let active = visible[0];
      visible.forEach((target) => {
        if (target.getBoundingClientRect().top <= line) active = target;
      });
      if (!active || active === current) return;
      current = active;
      links.forEach((link) => {
        if (link.getAttribute('href') === `#${active.id}`) {
          link.setAttribute('aria-current', 'true');
          if (onChange) onChange(link);
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };
    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          update();
        });
      },
      { passive: true }
    );
    update();
  };

  /* FAQ --------------------------------------------------------------------- */
  const initFaq = (root) => {
    const input = root.querySelector('[data-kp-faq-search]');
    const items = Array.from(root.querySelectorAll('[data-kp-faq-item]'));
    const groups = Array.from(root.querySelectorAll('[data-kp-faq-group]'));
    const empty = root.querySelector('[data-kp-faq-empty]');
    const texts = new Map(items.map((item) => [item, normalize(item.textContent)]));
    const openAtStart = new Map(items.map((item) => [item, item.open]));

    if (input) {
      input.addEventListener('input', () => {
        const query = normalize(input.value);
        let found = 0;
        items.forEach((item) => {
          const hit = !query || matches(texts.get(item), query);
          item.hidden = !hit;
          if (hit) found += 1;
          item.open = query ? hit && found <= 3 : openAtStart.get(item);
        });
        groups.forEach((group) => {
          group.hidden = Boolean(query) && !group.querySelector('[data-kp-faq-item]:not([hidden])');
        });
        if (empty) empty.hidden = !query || found > 0;
      });
    }

    const links = Array.from(root.querySelectorAll('[data-kp-faq-nav]'));
    spy(links, groups.filter((group) => group.id), (link) => {
      const rail = link.closest('ul');
      if (rail && rail.scrollWidth > rail.clientWidth) {
        rail.scrollTo({ left: link.parentElement.offsetLeft - 20, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    });

    // A link straight to a question (its id) opens it.
    const openFromHash = () => {
      const id = window.location.hash.slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (target && target.tagName === 'DETAILS') target.open = true;
    };
    window.addEventListener('hashchange', openFromHash);
    openFromHash();
  };

  /* Assembly videos: player (S-08) ------------------------------------------- */
  const initPlayer = (root, tilesFor) => {
    const dialog = root.querySelector('[data-kp-player]');
    if (!dialog || typeof dialog.showModal !== 'function') return null;
    const q = (sel) => dialog.querySelector(sel);
    const title = q('[data-kp-player-title]');
    const frame = q('[data-kp-player-frame]');
    const poster = q('[data-kp-player-poster]');
    const play = q('[data-kp-player-play]');
    const parts = q('[data-kp-player-parts]');
    const cat = q('[data-kp-player-cat]');
    const name = q('[data-kp-player-name]');
    const product = q('[data-kp-player-product]');
    const youtube = q('[data-kp-player-youtube]');
    const nextWrap = q('[data-kp-player-next-wrap]');
    const next = q('[data-kp-player-next]');
    let opener = null;
    let ids = [];
    let part = 0;

    const stop = () => {
      const iframe = frame.querySelector('iframe');
      if (iframe) iframe.remove();
      frame.classList.remove('is-playing');
    };

    const showPart = (i) => {
      stop();
      part = i;
      poster.src = `https://i.ytimg.com/vi/${ids[i]}/hqdefault.jpg`;
      youtube.href = `https://www.youtube.com/watch?v=${ids[i]}`;
      parts.querySelectorAll('button').forEach((b, n) => b.setAttribute('aria-pressed', String(n === i)));
    };

    const open = (tile, from) => {
      ids = (tile.dataset.ids || '').split(',').filter(Boolean);
      if (!ids.length) return false;
      opener = from || null;
      title.textContent = `${title.dataset.prefix || ''} ${tile.dataset.model || tile.dataset.title || ''}`.trim();
      cat.textContent = tile.dataset.cat || '';
      cat.hidden = !tile.dataset.cat;
      name.textContent = tile.dataset.title || '';
      product.href = tile.dataset.url || product.href;

      parts.innerHTML = '';
      parts.hidden = ids.length < 2;
      if (ids.length > 1) {
        ids.forEach((id, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'kp-player__part';
          b.textContent = (parts.dataset.label || 'Part [n] of [total]').replace('[n]', i + 1).replace('[total]', ids.length);
          b.addEventListener('click', () => showPart(i));
          parts.appendChild(b);
        });
      }
      showPart(0);

      // Up next: the following tiles of the same category
      const list = tilesFor(tile);
      const at = list.findIndex((t) => t.dataset.url === tile.dataset.url);
      const upcoming = [];
      for (let n = 1; n < list.length && upcoming.length < 4; n += 1) {
        const candidate = list[(Math.max(at, 0) + n) % list.length];
        if (candidate.dataset.url !== tile.dataset.url) upcoming.push(candidate);
      }
      next.innerHTML = '';
      upcoming.forEach((t) => {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'kp-player__next-item';
        if (t.dataset.img) {
          const img = document.createElement('img');
          img.src = t.dataset.img;
          img.alt = '';
          img.width = 104;
          img.height = 66;
          b.appendChild(img);
        }
        const span = document.createElement('span');
        span.textContent = t.dataset.title || '';
        b.appendChild(span);
        b.addEventListener('click', () => open(t, opener));
        li.appendChild(b);
        next.appendChild(li);
      });
      nextWrap.hidden = !upcoming.length;

      if (!dialog.open) {
        document.documentElement.classList.add('kp-player-open');
        dialog.showModal();
      }
      q('[data-kp-player-close]').focus();
      return true;
    };

    play.addEventListener('click', () => {
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(ids[part])}?autoplay=1&rel=0&modestbranding=1&cc_load_policy=0`;
      iframe.title = name.textContent || 'YouTube video';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.setAttribute('allowfullscreen', '');
      frame.appendChild(iframe);
      frame.classList.add('is-playing');
      iframe.focus();
    });

    q('[data-kp-player-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      stop();
      document.documentElement.classList.remove('kp-player-open');
      if (opener && typeof opener.focus === 'function') opener.focus();
    });

    return open;
  };

  /* Assembly videos: search, category chips, "+n more" (V-01, V-03, V-05, V-06) */
  const initAssembly = (root) => {
    const groups = Array.from(root.querySelectorAll('[data-kp-av-group]'));
    const gridTiles = Array.from(root.querySelectorAll('.kp-av__groups [data-kp-av-tile]'));
    const chips = Array.from(root.querySelectorAll('[data-kp-av-tab]'));
    const top = root.querySelector('[data-kp-av-top]');
    const others = root.querySelector('[data-kp-av-others]');
    const rows = Array.from(root.querySelectorAll('[data-kp-av-row]'));
    const input = root.querySelector('[data-kp-av-search]');
    const empty = root.querySelector('[data-kp-av-empty]');
    let current = 'all';

    const apply = () => {
      const query = normalize(input ? input.value : '');
      const searching = Boolean(query);
      root.classList.toggle('is-all', current === 'all' && !searching);
      let found = 0;
      groups.forEach((group) => {
        let visible = 0;
        group.querySelectorAll('[data-kp-av-tile]').forEach((tile) => {
          const hit = !searching || matches(normalize(tile.dataset.search), query);
          tile.hidden = !hit;
          if (hit) visible += 1;
        });
        const inTab = searching || current === 'all' || group.dataset.kpAvGroup === current;
        group.hidden = !inTab || visible === 0;
        if (!group.hidden) found += visible;
      });
      if (top) top.hidden = searching;
      if (others) {
        others.hidden = searching || current === 'all';
        rows.forEach((row) => {
          row.hidden = row.dataset.kpAvRow === current;
        });
      }
      if (empty) empty.hidden = !searching || found > 0;
      chips.forEach((chip) => chip.setAttribute('aria-pressed', String(!searching && chip.dataset.kpAvTab === current)));
    };

    const show = (id, scroll) => {
      current = id;
      if (input) input.value = '';
      apply();
      if (!scroll) return;
      const target = root.querySelector(`[data-kp-av-group="${id}"]`);
      if (target) target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    };

    // "+1 more" hides a single tile: show it instead.
    groups.forEach((group) => {
      const extras = group.querySelectorAll('[data-kp-av-extra]');
      if (extras.length !== 1) return;
      extras[0].removeAttribute('data-kp-av-extra');
      const more = group.querySelector('.kp-av__more-tile');
      if (more) more.remove();
    });

    root.classList.add('is-js');
    chips.forEach((chip) => chip.addEventListener('click', () => show(chip.dataset.kpAvTab, false)));
    root.addEventListener('click', (event) => {
      const link = event.target.closest('[data-kp-av-show]');
      if (!link) return;
      event.preventDefault();
      show(link.dataset.kpAvShow, true);
    });
    if (input) input.addEventListener('input', apply);

    const tilesFor = (tile) => {
      const inGrid = gridTiles.find((t) => t.dataset.url === tile.dataset.url) || tile;
      const group = inGrid.closest('[data-kp-av-group]');
      return group ? Array.from(group.querySelectorAll('[data-kp-av-tile]')) : gridTiles;
    };
    const open = initPlayer(root, tilesFor);
    if (open) {
      root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-kp-av-open]');
        if (!trigger) return;
        const tile = trigger.closest('[data-kp-av-tile]');
        if (tile && open(tile, trigger)) event.preventDefault();
      });
    }

    // A link to a category (#av-shoe-storage) opens that category.
    const hash = window.location.hash.slice(1);
    if (hash && groups.some((group) => group.dataset.kpAvGroup === hash)) show(hash, true);
    else apply();
  };

  /* Reviews page: "Write a review" in the hero opens the Judge.me form of the widget below */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-kp-write-review]');
    if (!button || event.defaultPrevented) return;
    const judgeMe = document.querySelector('.jdgm-all-reviews-widget-v2025 .jm-action-buttons__button, .jdgm-write-rev-link');
    if (judgeMe) {
      event.preventDefault();
      judgeMe.click();
    }
  });

  const init = () => {
    document.querySelectorAll('[data-kp-video]').forEach(initVideo);
    document.querySelectorAll('[data-kp-faq]').forEach(initFaq);
    document.querySelectorAll('[data-kp-av]').forEach(initAssembly);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
